import { useEffect, useRef } from 'react'
import { createConsumer, Cable } from '@rails/actioncable'
import type { ChannelEvent } from '@/types/conversations'

interface ConversationChannelOptions {
  conversationId: number
  onEvent: (event: ChannelEvent) => void
  onConnected?: () => void
  onDisconnected?: () => void
}

/**
 * Hook to subscribe to ConversationChannel via ActionCable
 * Receives real-time events from ProcessMessageJob broadcasts
 */
export function useConversationChannel({
  conversationId,
  onEvent,
  onConnected,
  onDisconnected
}: ConversationChannelOptions) {
  const consumerRef = useRef<Cable | null>(null)
  const channelRef = useRef<any>(null)

  // Store latest callbacks in refs to avoid recreating subscription
  const onEventRef = useRef(onEvent)
  const onConnectedRef = useRef(onConnected)
  const onDisconnectedRef = useRef(onDisconnected)

  // Update refs when callbacks change
  useEffect(() => {
    onEventRef.current = onEvent
    onConnectedRef.current = onConnected
    onDisconnectedRef.current = onDisconnected
  })

  useEffect(() => {
    // Create ActionCable consumer
    consumerRef.current = createConsumer()

    // Subscribe to ConversationChannel
    channelRef.current = consumerRef.current.subscriptions.create(
      {
        channel: 'ConversationChannel',
        id: conversationId
      },
      {
        received: (data: ChannelEvent) => {
          console.log('📡 Channel event received:', data.type, data)
          onEventRef.current(data)
        },
        connected: () => {
          console.log('✅ Connected to ConversationChannel', conversationId)
          onConnectedRef.current?.()
        },
        disconnected: () => {
          console.log('❌ Disconnected from ConversationChannel', conversationId)
          onDisconnectedRef.current?.()
        },
        rejected: () => {
          console.error('🚫 ConversationChannel subscription rejected', conversationId)
        }
      }
    )

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        console.log('🧹 Unsubscribing from ConversationChannel', conversationId)
        channelRef.current.unsubscribe()
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect()
      }
    }
  }, [conversationId])

  return {
    channel: channelRef.current,
    consumer: consumerRef.current,
    isConnected: !!channelRef.current
  }
}
